import { useCallback, useSyncExternalStore } from "react";
import { isAbortError } from "../../../api/http/httpClient";
import { getInbox, type Inbox } from "../../../api/content/reviewCollaborationApi";
import { reportUiError, toUiError } from "../../../errors/uiError";
import type { LoadState } from "../../../shared/loading/useLoad";
import { onChangeSetsChanged } from "../../changeSets/changeSetEvents";

/**
 * The project's review inbox (CU-10's sections, ADR-0031 D4), the one reader of it for every
 * view that shows it: the Threads sidebar, which is the inbox (F108), and the badge on the
 * Threads tab, so both always show the same snapshot. As the host offers' inbox
 * (`incomingHostOffers`): read while shown, again every two minutes while the window is
 * visible and when it gets the focus back, and whenever a change set changes here or in the
 * other pane, so "My open proposals" shows a proposal as soon as it is saved (TR-B3-04). A
 * failed re-read keeps what was read; only a first read that fails shows the failure.
 */

const READ_EVERY_MILLISECONDS = 120_000;

interface ProjectInboxEntry {
  state: LoadState<Inbox>;
  readonly listeners: Set<() => void>;
  reading: AbortController | null;
  stop: (() => void) | null;
}

const LOADING: LoadState<Inbox> = { status: "loading" };
const inboxes = new Map<string, ProjectInboxEntry>();

function notify(entry: ProjectInboxEntry, state: LoadState<Inbox>): void {
  entry.state = state;
  for (const listener of entry.listeners) listener();
}

function read(projectId: string): void {
  const entry = inboxes.get(projectId);
  if (entry === undefined) return;
  entry.reading?.abort();
  const controller = new AbortController();
  entry.reading = controller;
  getInbox(projectId, controller.signal).then(
    (value) => {
      if (controller.signal.aborted) return;
      entry.reading = null;
      notify(entry, { status: "loaded", value });
    },
    (caught: unknown) => {
      if (controller.signal.aborted || isAbortError(caught)) return;
      entry.reading = null;
      const error = toUiError(caught);
      reportUiError(error);
      if (entry.state.status !== "loaded") notify(entry, { status: "failed", error });
    },
  );
}

/** Read the project's inbox again now, for every place that shows it. */
export function refreshReviewInbox(projectId: string): void {
  read(projectId);
}

/** Read it again as a view shows it, unless a read is already on its way (the first one, say). */
export function revalidateReviewInbox(projectId: string): void {
  if (inboxes.get(projectId)?.reading === null) read(projectId);
}

function subscribeTo(projectId: string, listener: () => void): () => void {
  let entry = inboxes.get(projectId);
  if (entry === undefined) {
    entry = { state: LOADING, listeners: new Set(), reading: null, stop: null };
    inboxes.set(projectId, entry);
  }
  const shown = entry;
  shown.listeners.add(listener);
  if (shown.listeners.size === 1) {
    const again = () => { read(projectId); };
    const timer = setInterval(() => { if (!document.hidden) again(); }, READ_EVERY_MILLISECONDS);
    window.addEventListener("focus", again);
    const unsubscribe = onChangeSetsChanged(again);
    shown.stop = () => {
      clearInterval(timer);
      window.removeEventListener("focus", again);
      unsubscribe();
    };
    again();
  }
  return () => {
    shown.listeners.delete(listener);
    if (shown.listeners.size > 0) return;
    // Nobody shows this project's inbox any more: forget it.
    shown.reading?.abort();
    shown.stop?.();
    inboxes.delete(projectId);
  };
}

/** The project's inbox as it was last read; `null` without a project. */
export function useReviewInbox(projectId: string | null): LoadState<Inbox> | null {
  const subscribe = useCallback((listener: () => void) => (projectId === null ? () => undefined : subscribeTo(projectId, listener)), [projectId]);
  const snapshot = useCallback(() => (projectId === null ? null : (inboxes.get(projectId)?.state ?? LOADING)), [projectId]);
  return useSyncExternalStore(subscribe, snapshot);
}
